//#region src/service-provider/ServiceRegistry.ts
/**
* Central service container for dependency injection
*
* Usage:
* ```typescript
* // In app initialization (e.g., main.ts)
* import { services } from "pd-helper-app";
* import { createFirebaseProvider } from "pd-provider-firebase";
*
* services.initialize(createFirebaseProvider(firebaseConfig));
*
* // In components/services
* import { services } from "pd-helper-app";
*
* const user = services.auth.getCurrentUser();
* ```
*/
var ServiceRegistry = class {
	constructor() {
		this.provider = null;
	}
	/**
	* Initialize the service registry with a provider
	* @param provider The service provider to use
	* @throws Error if already initialized
	*/
	initialize(provider) {
		if (this.provider !== null) throw new Error("ServiceRegistry already initialized. Call reset() first if re-initialization is needed.");
		this.provider = provider;
	}
	/**
	* Check if the registry has been initialized
	*/
	get isInitialized() {
		return this.provider !== null;
	}
	/**
	* Ensure the registry is initialized before accessing providers
	* @throws Error if not initialized
	*/
	ensureInitialized() {
		if (this.provider === null) throw new Error("ServiceRegistry not initialized. Call services.initialize(provider) before accessing services.");
	}
	/**
	* Get the auth provider
	*/
	get auth() {
		this.ensureInitialized();
		return this.provider.auth;
	}
	/**
	* Get the functions provider
	*/
	get functions() {
		this.ensureInitialized();
		return this.provider.functions;
	}
	/**
	* Get the storage provider
	*/
	get storage() {
		this.ensureInitialized();
		return this.provider.storage;
	}
	/**
	* Get the database provider (optional)
	* @returns Database provider or undefined if not configured
	*/
	get database() {
		this.ensureInitialized();
		return this.provider?.database;
	}
	/**
	* Get a NAMED database provider.
	*
	* `database` returns the primary one — the database an app works with by
	* default. Apps that read from several (say, their own plus a shared one)
	* address the others by name.
	*
	* Throws rather than returning undefined: A missing name is a configuration
	* mistake, and it should surface where it happens, not as an empty list
	* three layers further on.
	*
	* @param name Logical name from the provider configuration
	*/
	databaseNamed(name) {
		this.ensureInitialized();
		const database = this.provider?.databases?.[name];
		if (!database) {
			const bekannt = Object.keys(this.provider?.databases ?? {});
			throw new Error(`No database configured under the name "${name}". ` + (bekannt.length ? `Configured: ${bekannt.join(", ")}.` : "No named databases configured at all."));
		}
		return database;
	}
	/**
	* Reset the registry (primarily for testing)
	*
	* Warning: This should only be used in tests.
	* In production, providers should be initialized once at app startup.
	*/
	reset() {
		this.provider = null;
	}
};
/**
* Global service registry instance
*
* This is the main entry point for accessing services throughout the application.
*/
var services = new ServiceRegistry();
//#endregion
export { ServiceRegistry, services };
